
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from "@/components/ui/button";
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { trackPhoneCallConversion } from '@/utils/trackPhoneCall';

const VangedeLocation = () => {
  const testimonials = [
    {
      name: "Kristian Jensen",
      location: "Vangede",
      text: "Oscar har en fantastisk måde at forklare musik på. Han tilpasser undervisningen, så den passer præcist til mit niveau og mine interesser. Det har gjort klaverundervisningen til et højdepunkt i min uge.",
      stars: 5
    },
    {
      name: "Mette Andersen",
      location: "Vangede",
      text: "Min datter har spillet klaver hos Oscar i et år nu, og hendes fremskridt er imponerende. Oscar har en særlig evne til at motivere og gøre undervisningen sjov, selv når det handler om at lære de tekniske aspekter.",
      stars: 5
    },
    {
      name: "Henrik Poulsen",
      location: "Vangede",
      text: "Som voksen begynder var jeg nervøs for at starte med klaverundervisning, men Oscar har gjort det til en fantastisk oplevelse. Hans tålmodighed og pædagogiske tilgang har givet mig stor fremgang på kort tid.",
      stars: 5
    }
  ];

  const features = [
    {
      title: "Nem adgang fra Vangede",
      description: "Undervisningslokalet er let at nå med både bil og offentlig transport fra Vangede.",
      icon: "map-pin"
    },
    {
      title: "Fleksible undervisningstider",
      description: "Mulighed for undervisning på forskellige tidspunkter, der passer ind i din hverdag.",
      icon: "clock"
    },
    {
      title: "For alle aldersgrupper",
      description: "Undervisning tilbydes til både børn, unge og voksne fra Vangede-området.",
      icon: "users"
    },
    {
      title: "Personlig tilgang",
      description: "Undervisningen tilpasses individuelt til dit niveau og dine musikalske mål.",
      icon: "settings"
    }
  ];

  return (
    <Layout>
      <LocationHero 
        title="Klaverundervisning i Vangede"
        description="Professionel klaverundervisning med Oscar Gilbert for alle fra Vangede. Undervisning i behagelige omgivelser på Niels Finsens Alle 86 i Søborg, let tilgængeligt fra Vangede."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning i <span className="text-piano-gold">Vangede</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Oscar Gilbert tilbyder professionel klaverundervisning for elever fra Vangede på sin adresse på Niels Finsens Alle 86 i Søborg, som er let tilgængelig fra Vangede med både bil og offentlig transport.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Uanset om du er helt nybegynder eller en øvet pianist, får du personlig og engageret undervisning, der tager udgangspunkt i dine musikalske ønsker og ambitioner.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Undervisningslokalet er indrettet med fokus på god akustik og en rar atmosfære, hvilket skaber de bedste betingelser for din musikalske udvikling.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book gratis prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/undervisning">Se undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Vangede" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Vangede" title="Hvad siger mine elever i Vangede" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, let tilgængeligt fra Vangede.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5659.685620868027!2d12.519626863955608!3d55.740297566283786!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743770954052!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport fra Vangede:</strong></p>
                    <p className="text-gray-700">• 5 minutters gang fra Vangede Station</p>
                    <p className="text-gray-700">• Gode busforbindelser</p>
                    <p className="text-gray-700">• Gratis parkering foran bygningen</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4526848410" className="text-piano-gold hover:underline" onClick={trackPhoneCallConversion}>+45 26 84 84 10</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Vangede" />
    </Layout>
  );
};

export default VangedeLocation;
